<script>
import EventsTable from '../events/EventsTable'
import ImageTemplatesTable from '../image-templates/ImageTemplatesTable'
import TextTemplatesTable from '../text-templates/TextTemplatesTable'
import AdSetSettingsForm from './AdSetSettingsForm'
import AdCreativeSettingsForm from './AdCreativeSettingsForm'
import AdSettingsForm from './AdSettingsForm'
import AdPreview from './AdPreview'
import { currencyStringToNumber } from '../utils/currencyStringToNumber'
import { pluralize } from '../utils/pluralize'
import { combineArrays } from '../utils/combineArrays'

const textAndImageTemplateCombinations = (textTemplates, imageTemplates) =>
  combineArrays(textTemplates, imageTemplates).map(([textTemplate, imageTemplate]) => ({
    textTemplate,
    imageTemplate
  }))

export default {
  name: 'CampaignReviewScreen',
  components: {
    EventsTable,
    TextTemplatesTable,
    ImageTemplatesTable,
    AdSetSettingsForm,
    AdCreativeSettingsForm,
    AdSettingsForm,
    AdPreview
  },
  props: {
    events: { type: Array, required: true },
    textTemplates: { type: Array, required: true },
    imageTemplates: { type: Array, required: true },
    campaignSettings: { type: Object, required: true },
    adSetSettings: { type: Object, required: true },
    adCreativeSettings: { type: Object, required: true },
    adSettings: { type: Object, required: true }
  },
  computed: {
    textAndImageTemplateCombinations() {
      return textAndImageTemplateCombinations(this.textTemplates, this.imageTemplates)
    },
    summaryStats() {
      return this.state.adSets.reduce(
        (stats, adSet) => {
          const isDaily = adSet.settings.budgetType === 'DAILY'
          const isLifeTime = adSet.settings.budgetType === 'LIFETIME'
          return {
            adSetCount: stats.adSetCount + 1,
            adCount: stats.adCount + adSet.ads.filter(ad => ad.enabled).length,
            totalLifeTimeBudget: stats.totalLifeTimeBudget + (isLifeTime ? adSet.settings.budget : 0),
            totalDailyBudget: stats.totalDailyBudget + (isDaily ? adSet.settings.budget : 0),
            lifeTimeBudgetAdSetCount: stats.lifeTimeBudgetAdSetCount + (isLifeTime ? 1 : 0),
            dailyBudgetAdSetCount: stats.dailyBudgetAdSetCount + (isDaily ? 1 : 0)
          }
        },
        {
          adSetCount: 0,
          adCount: 0,
          totalLifeTimeBudget: 0,
          totalDailyBudget: 0,
          lifeTimeBudgetAdSetCount: 0,
          dailyBudgetAdSetCount: 0
        }
      )
    }
  },
  data() {
    const data = {
      state: {
        adSets: this.events.map(event => ({
          event,
          settings: { ...this.adSetSettings },
          ads: textAndImageTemplateCombinations(this.textTemplates, this.imageTemplates).map(
            ({ textTemplate, imageTemplate }) => ({
              enabled: true,
              imageTemplate,
              renderedText: null,
              renderedImage: null,
              textTemplate,
              creative: {
                settings: {
                  ...this.adCreativeSettings
                }
              },
              settings: { ...this.adSettings }
            })
          )
        }))
      }
    }
    return data
  },
  mounted() {
    this.$emit('input', this.state)
  },
  methods: {
    initialState() {
      return {
        adSets: this.events.map(event => ({
          event,
          settings: { ...this.adSetSettings },
          ads: this.textAndImageTemplateCombinations.map(({ textTemplate, imageTemplate }) => ({
            enabled: true,
            imageTemplate,
            renderedText: null,
            renderedImage: null,
            textTemplate,
            creative: {
              settings: {
                ...this.adCreativeSettings
              }
            },
            settings: { ...this.adSettings }
          }))
        }))
      }
    },
    initializeState() {
      // TODO: update this to keep some of the previous settings
      this.state = this.initialState()
    },
    currencyStringToNumber,
    pluralize
  },
  watch: {
    events: function() {
      this.initializeState()
    },
    textTemplates: function() {
      this.initializeState()
    },
    imageTemplates: function() {
      this.initializeState()
    },
    campaignSettings: function() {
      this.initializeState()
    },
    adSetSettings: function() {
      this.initializeState()
    },
    adCreativeSettings: function() {
      this.initializeState()
    },
    adSettings: function() {
      this.initializeState()
    },
    state: {
      deep: true,
      handler: function(oldValue, newValue) {
        this.$emit('input', newValue)
      }
    }
  }
}
</script>

<template>
  <div>
    <div class="review-summary-box">
      <v-alert outlined color="#666" title="Summary">
        <p class="my-4">
          You have selected {{ pluralize(events.length, 'event') }},
          {{ pluralize(textTemplates.length, 'text template') }} and
          {{ pluralize(imageTemplates.length, 'image template') }}.
        </p>
        <p class="my-4">
          This will generate {{ pluralize(events.length, 'ad set') }} (one for each event) with
          {{ pluralize(textTemplates.length * imageTemplates.length, 'ad') }} each (one for each combination of image
          template and text template, {{ events.length * textTemplates.length * imageTemplates.length }} in total).
        </p>
        <p class="my-4" v-if="!isNaN(adSetSettings.budget)">
          With the selected {{ adSetSettings.budgetType === 'LIFETIME' ? 'lifetime' : 'daily' }} budget of ${{
            (adSetSettings.budget / 100).toFixed(2)
          }}
          per ad set, this results in a total
          {{ adSetSettings.budgetType === 'LIFETIME' ? 'lifetime' : 'daily' }} budget of ${{
            ((adSetSettings.budget * events.length) / 100).toFixed(2)
          }}.
        </p>
        <p class="my-4">
          You can now review and customize the settings for each ad set and ad as well as enable or disable individual
          ads. At the bottom of this page, you will see a final summary based on these customizations.
        </p>
      </v-alert>
    </div>
    <!-- Events -->
    <h3 class="my-4">Events</h3>
    <div class="my-4">
      <events-table :items="events" />
    </div>
    <!-- Text templates -->
    <h3 class="my-4">Text Templates</h3>
    <div class="my-4">
      <text-templates-table :items="textTemplates" :showActions="false" />
    </div>
    <!-- Image templates -->
    <h3 class="my-4">Image Templates</h3>
    <div class="my-4">
      <image-templates-table :items="imageTemplates" :showActions="false" />
    </div>
    <!-- Ads -->
    <h3 class="my-4">{{ state.adSets.length > 1 ? 'Ad Sets' : 'Ad Set' }}</h3>
    <ul class="ad-sets-list my-4">
      <li class="block" :key="adSetIndex" v-for="(adSet, adSetIndex) in state.adSets">
        <h4 class="my-4" v-if="state.adSets.length > 1">Ad Set {{ adSetIndex + 1 }}</h4>
        <p class="small-text-header">Event</p>
        <router-link :to="'/mobilizev2/events/' + adSet.event.id">{{ adSet.event.id }}</router-link>
        <div class="my-4">
          <ad-set-settings-form :event="adSet.event" v-model="adSet.settings" />
        </div>
        <h5 class="my-4">{{ adSet.ads.length > 1 ? 'Ads' : 'Ad' }}</h5>
        <ul class="ad-list">
          <li class="block" :key="adIndex" v-for="(ad, adIndex) in adSet.ads">
            <h6 class="my-4" v-if="adSet.ads.length > 1">Ad {{ adIndex + 1 }}</h6>
            <div class="my-4">
              <v-checkbox v-model="ad.enabled" label="Enable" />
            </div>
            <ad-preview
              :event="adSet.event"
              :image-template="ad.imageTemplate"
              :text-template="ad.textTemplate"
              :ad-creative-settings="ad.creative.settings"
            />
            <div class="my-4">
              <ad-creative-settings-form v-model="ad.creative.settings" />
            </div>
            <div class="my-4">
              <ad-settings-form v-model="ad.settings" />
            </div>
          </li>
        </ul>
      </li>
    </ul>
    <h3 class="my-4">Summary</h3>
    <div class="my-4">
      <v-alert color="#666" outlined>
        This will create a campaign with
        {{
          campaignSettings.lifeTimeBudget
            ? `a lifetime budget of $${campaignSettings.lifeTimeBudget}`
            : 'an unlimited lifetime budget'
        }}, {{ pluralize(summaryStats.adSetCount, 'ad set') }} and {{ pluralize(summaryStats.adCount, 'ad') }} in total.
        There are {{ pluralize(summaryStats.lifeTimeBudgetAdSetCount, 'ad set') }} with a total lifetime budget of in
        total ${{ (summaryStats.totalLifeTimeBudget / 100).toFixed(2) }} and
        {{ pluralize(summaryStats.dailyBudgetAdSetCount, 'ad set') }} with a total daily budget of ${{
          (summaryStats.totalDailyBudget / 100).toFixed(2)
        }}.
      </v-alert>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/initial-variables.scss';

.block {
  display: block;
}

.small-text-header {
  font-size: 12px;
  color: #666666;
  margin-bottom: 0;
}

.review-summary-box {
  margin-bottom: 50px;
}

.ad-list,
.ad-sets-list {
  margin-left: 0;
  padding-left: 0;

  li {
    margin: 0;
  }
}

.ad-sets-list {
  & > li {
    background-color: $light-gray;
    padding: 20px 40px;
    border-radius: 10px;
    margin-bottom: 40px;
  }

  .theme--light.v-data-table {
    background-color: $light-gray;
  }
}

h3.my-4 {
  margin: 50px 0 0 0 !important;
}

.featuredImage {
  max-height: 300px;
  max-width: 100%;
}
</style>
